import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  MarketingEmbedMessage,
  MarketingMessage,
  MarketingPorts,
} from '@/types/embed';
import { isString } from '@/utils/typeGuards/isString';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { clearReservation, setReservation } from '@/store/slices/reservation';
import { incrementLoadedEvents } from '@/store/slices/events';

export interface EmbedHelperImperativeRef {
  getMarketingPorts: () => MarketingPorts;
  openCart: () => void;
  openCheckout: () => void;
  sendReservation: (data: {
    token: string;
    secret: string;
    reservation_data: { attributes: any };
  }) => void;
  clearCurrentReservation: () => void;
  listingPriceLevelsUpdate: () => void;
}

export const EmbedMarketingHandler = forwardRef<EmbedHelperImperativeRef>(
  (_, ref) => {
    const dispatch = useAppDispatch();

    const marketingPorts = useRef<MarketingPorts>({});

    let activeListing: string = '';

    const getMarketingPorts = () => marketingPorts.current;

    const getFirstMarketingPort = () => {
      if (!Object.keys(marketingPorts.current).length) return;
      if (activeListing) {
        return marketingPorts.current[activeListing];
      }

      return marketingPorts.current[Object.keys(marketingPorts.current)[0]];
    };

    useImperativeHandle(
      ref,
      () => ({
        getMarketingPorts,
        openCart,
        openCheckout,
        sendReservation,
        clearCurrentReservation,
        listingPriceLevelsUpdate,
      }),
      [],
    );

    // Send reservation data to ports
    const sendReservation = (data: {
      token: string;
      secret: string;
      reservation_data: { attributes: any };
    }) => {
      dispatch(setReservation(data.reservation_data.attributes));

      Object.keys(marketingPorts.current).forEach((key) => {
        marketingPorts.current[key].postMessage({
          multiEventParent: true,
          action: 'reservationDataSet',
          token: data.token,
          secret: data.secret,
          reservationData: data.reservation_data,
        });
      });
    };

    const listingPriceLevelsUpdate = () => {
      Object.keys(marketingPorts.current).forEach((key) => {
        marketingPorts.current[key].postMessage({
          multiEventParent: true,
          action: 'listingPriceLevelsUpdate',
        });
      });
    };

    const clearCurrentReservation = () => {
      dispatch(clearReservation());

      Object.keys(marketingPorts.current).forEach((key) => {
        marketingPorts.current[key].postMessage({
          multiEventParent: true,
          action: 'reservationClear',
        });
      });
    };

    const openCheckout = () => {
      const first = getFirstMarketingPort();

      first?.postMessage({
        action: 'openCheckout',
        multiEventParent: true,
      });
    };

    const openCart = () => {
      const first = getFirstMarketingPort();

      first?.postMessage({
        action: 'openCart',
        multiEventParent: true,
      });
    };

    const onMessage = useCallback(
      (e: MessageEvent<MarketingMessage | string>) => {
        if (
          !isString(e.data) ||
          !(e.data as string).startsWith('embedStatePort') ||
          !e.ports ||
          !Object.keys(e.ports).length
        ) {
          return;
        }

        const splitData = (e.data as string).split('__');
        const tempPort = e.ports[0];

        tempPort.onmessage = function (embedMessage: MarketingEmbedMessage) {
          const { data } = embedMessage;

          if (data.token && data.action === 'reservationDataSet') {
            dispatch(setReservation(data.reservationData.attributes));
            activeListing = data.listingId;

            Object.keys(marketingPorts.current).forEach((key) => {
              if (Number(key) !== Number(data.listingId)) {
                marketingPorts.current[key].postMessage({
                  multiEventParent: true,
                  action: 'reservationDataSet',
                  token: data.token,
                  secret: data.secret,
                  reservationData: data.reservationData,
                });
              }
            });
          } else if (data.action === 'reservationClear') {
            dispatch(clearReservation());

            activeListing = data.listingId;
            Object.keys(marketingPorts.current).forEach((key) => {
              if (Number(key) !== Number(data.listingId)) {
                marketingPorts.current[key].postMessage({
                  multiEventParent: true,
                  action: 'reservationClear',
                });
              }
            });
            self.postMessage('embed__reservationCleared__' + data.token, '*');
          } else if (data.action === 'reservationComplete') {
            dispatch(clearReservation());
          } else if (data.action === 'salesUpdated') {
            activeListing = data.listingId;
            Object.keys(marketingPorts.current).forEach((key) => {
              if (Number(key) !== Number(data.listingId)) {
                marketingPorts.current[key].postMessage({
                  state: data.state,
                  setter: data.setter,
                  action: 'updateSales',
                });
              }
            });
          } else if (data.action === 'eventFetched') {
            dispatch(incrementLoadedEvents());
          }
        };
        marketingPorts.current[splitData[1]] = tempPort; // just to keep track
      },
      [],
    );

    useEffect(() => {
      window.addEventListener('message', onMessage);

      return () => {
        window.removeEventListener('message', onMessage);
      };
    }, []);

    return null;
  },
);
